import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input"];

  static values = {
    errorMessage: { type: String, default: "" },
  };

  connect() {
    this.validateOnLoad();
  }

  validateOnLoad() {
    if (this.inputTarget.value) {
      this.validate();
    }
  }

  validate() {
    const input = this.inputTarget;
    const { value } = input;

    // Check browser's native validation (catches bad input in date fields)
    if (input.validity.badInput) {
      this.markInvalid();
      return false;
    }

    if (!value || value === "") {
      this.markValid();
      return true;
    }

    // Check if it's a valid ISO date from native input (YYYY-MM-DD)
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (isoDateRegex.test(value)) {
      // Validate the date is actually valid
      if (this.isValidDate(value)) {
        this.markValid();
        return true;
      }
      this.markInvalid();
      return false;
    }

    // Invalid format
    this.markInvalid();
    return false;
  }

  // eslint-disable-next-line class-methods-use-this
  isValidDate(dateString) {
    const date = new Date(dateString);

    // Check if date is valid
    if (Number.isNaN(date.getTime())) {
      return false;
    }

    // new Date("YYYY-MM-DD") parses as UTC midnight. Local-time getters
    // (getDate etc.) shift that instant backwards for any UTC-n timezone (all
    // of the Americas), so getDate() returns the day *before* the one in the
    // string. The comparison would fail, setCustomValidity() would fire, and
    // the browser would silently block the form submission without ever
    // reaching the server.
    // Using UTC getters keeps everything in the same timezone as the parse.
    const [year, month, day] = dateString
      .split("-")
      .map((n) => parseInt(n, 10));
    return (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  }

  markInvalid() {
    // Add error styling
    this.inputTarget.classList.add(
      "border-red-500",
      "focus:border-red-500",
      "focus:ring-red-500"
    );
    this.inputTarget.classList.remove("border-gray-300");

    // Set custom validity to prevent form submission
    this.inputTarget.setCustomValidity(
      this.errorMessageValue || "Invalid date"
    );
  }

  markValid() {
    // Remove error styling
    this.inputTarget.classList.remove(
      "border-red-500",
      "focus:border-red-500",
      "focus:ring-red-500"
    );
    this.inputTarget.classList.add("border-gray-300");

    // Clear custom validity to allow form submission
    this.inputTarget.setCustomValidity("");
  }

  // Called on blur
  handleChange() {
    this.validate();
  }
}
